import { Text, useStyledSystem } from "@hopper-ui/components";
import { SearchIcon } from "@hopper-ui/icons";
import { Breakpoints } from "@hopper-ui/styled-system";
import { Shortcut, useMinWidth, useShortcut } from "@sg-protect/components";
import { clsx } from "clsx";
import { Button } from "react-aria-components";
import { type ShellTFunction, useShellTranslation } from "../../../i18n/i18next.ts";
import styles from "./CommandPaletteTrigger.module.css";

export const COMMAND_PALETTE_WIDTH = "33.75rem";

export interface CommandPaletteTriggerProps {
    shortcut: string;
    tenantName?: string;
    query?: string;
}

function getTriggerLabel(query: string | undefined, tenantName: string | undefined, t: ShellTFunction) {
    const trimmedQuery = query?.trim();

    if (trimmedQuery) {
        return t("CommandPaletteTrigger.withQuery", { query: trimmedQuery });
    }

    if (tenantName) {
        return t("CommandPaletteTrigger.inTenant", { tenantName });
    }

    return t("CommandPalette.search");
}

export function CommandPaletteTrigger({ shortcut, tenantName, query }: CommandPaletteTriggerProps) {
    const { t } = useShellTranslation();
    const { ariaKeyShortcuts } = useShortcut(shortcut);
    const showLabel = useMinWidth(Breakpoints.xs);

    const { stylingProps } = useStyledSystem({
        display: "inline-flex",
        alignItems: "center",
        gap: "inline-sm",
        paddingX: { base: "inset-sm", sm: "inset-md" },
        color: "neutral",
        backgroundColor: "neutral",
        backgroundColorHover: "neutral-hover",
        border: "neutral-weak",
        borderHover: "neutral-hover",
        borderRadius: "rounded-md",
        cursor: "pointer",
        overflow: "hidden",
        minWidth: "core_320",
        UNSAFE_width: showLabel ? COMMAND_PALETTE_WIDTH : "auto",
        UNSAFE_height: "2rem"
    });

    return (
        <Button
            className={clsx(stylingProps.className, styles.trigger)}
            style={stylingProps.style}
            render={props => (
                <button
                    type="button"
                    {...props}
                    aria-keyshortcuts={ariaKeyShortcuts}
                    aria-label={showLabel ? undefined : t("CommandPalette.search")}
                />
            )}
        >
            <SearchIcon size="sm" fill="neutral-weak" flexShrink={0} />
            {showLabel && (
                <Text
                    size="sm"
                    flexGrow={1}
                    textAlign="start"
                    color="neutral-weakest"
                    minWidth={0}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                >
                    {getTriggerLabel(query, tenantName, t)}
                </Text>
            )}
            <Shortcut keys={shortcut} display={{ base: "none", sm: "inline-flex" }} />
        </Button>
    );
}
